import { useEffect } from "react";
import Head from "next/head";
import Link from "next/link";

import useLocalStorage from "hooks/useLocalStorage";
import { getCanonicalSiteUrl } from "@/lib/regions/client";

import { db } from "@/lib/database";
import { asc, eq } from "drizzle-orm";
import { wellington_searchable_stops } from "@/schemas/extras";

import Nav from "@/components/Nav";
import Search from "@/components/Search";
import SnapperWidget from "@/components/SnapperWidget";
import Favourites from "@/components/Favourites";

import LocationMarker from "@catchy/assets/svgs/location-mono.svg";

export async function getStaticProps() {
  async function getStops() {
    return db
      .select({
        stop_id: wellington_searchable_stops.stop_id,
        stop_name: wellington_searchable_stops.stop_name,
      })
      .from(wellington_searchable_stops)
      .where(eq(wellington_searchable_stops.stop_type, "train"))
      .orderBy(asc(wellington_searchable_stops.stop_name));
  }

  const stops = await getStops();

  return { props: { stops }, revalidate: 300 };
}

export default function TrainsPage({
  isApple,
  isClient,
  mapsSupported,
  preferredStopNames,
  stops,
}) {
  const siteUrl = getCanonicalSiteUrl();
  const [favourites, setFavourites] = useLocalStorage("favourites", []);
  const [lastMain, setLastMain] = useLocalStorage("last_main", null);

  useEffect(() => {
    setLastMain("train");
  }, [setLastMain]);

  const title = "Wellington train departures and timetables — Catchy";
  const description =
    "Find Wellington train departures, station times, and timetables.";
  const canonicalUrl = `${siteUrl}/train`;

  return (
    <>
      <Head>
        <title key="title">{title}</title>

        <meta name="twitter:text:title" content={title} key="twitterTitle" />
        <meta
          name="twitter:text:description"
          content={description}
          key="twitterDescription"
        />

        <meta property="og:title" content={title} key="ogTitle" />
        <meta
          property="og:description"
          content={description}
          key="ogDescription"
        />
        <meta name="description" content={description} key="description" />
        <meta
          property="og:image"
          content="https://catchy.nz/share/share-train.png"
          key="ogImage"
        />
        <meta property="og:url" content={canonicalUrl} key="ogUrl" />
        <link rel="canonical" href={canonicalUrl} key="canonicalUrl" />
      </Head>
      <Nav current="train" />
      <div className="mb-20 flex flex-col pt-4">
        <Search
          stops={stops}
          type="train"
          preferredStopNames={preferredStopNames}
          isLoading={false}
        />

        {isClient && mapsSupported ? (
          <div className="px-[20px] py-2">
            <Link
              href="/map?from=train"
              className="flex w-full items-center justify-center rounded-full py-2 pl-4 pr-6 text-center font-semibold text-blue-600 ring-1 ring-blue-500/50 transition-colors duration-200 hover:bg-blue-400/10 focus:outline-none focus-visible:ring dark:text-blue-400 dark:ring-blue-400/50"
            >
              <LocationMarker className="mr-2 h-[1.125rem] w-[1.125rem]" />
              Map of stops and current trips
            </Link>
          </div>
        ) : (
          ""
        )}

        <div className="mt-4 flex flex-col gap-8">
          <SnapperWidget isApple={isApple} type="train" isClient={isClient} />
          <Favourites
            isApple={isApple}
            isClient={isClient}
            preferredStopNames={preferredStopNames}
            stops={stops}
            type="train"
          />
        </div>
      </div>
    </>
  );
}
